import { CalendarIcon } from "@heroicons/react/24/solid"
import React, { useState } from "react"
import { useQuery } from "urql"

import Breadcrumbs from "../../../components/shared/Breadcrumbs"
import { Box, Divider } from "../../../components/shared/Layout"
import Typography from "../../../components/shared/Typography"
import { ToastProvider } from "../../../contexts/ToastContext"
import GraphQLProvider from "../../../providers/GraphQLProvider"
import AllDayBlockingSettings from "../ProCalendarSettings/AllDayBlockingSettings"

import AccountCard from "./AccountCard"
import ConnectProviderButtons from "./ConnectProviderButtons"
import DisconnectConfirmModal from "./DisconnectConfirmModal"
import { CALENDAR_INTEGRATION_STATUS_QUERY } from "./graphql"
import ReadCalendarsFlyout from "./ReadCalendarsFlyout"
import { visibleConnections } from "./selectors"
import { CalendarConnectionSummary, CalendarIntegrationStatusQueryData, CalendarIntegrationsPageProps } from "./types"
import WriteCalendarFlyout from "./WriteCalendarFlyout"

// Provider-neutral calendar settings (Plan 08 Slice D). SSR compatibility
// props render the first paint; all connection/calendar/migration detail
// comes from the calendarIntegrationStatus GraphQL query. This page never
// calls legacy Cronofy mutations.
const CalendarIntegrationsBody = ({ externalCalendarAllDayBlocking }: CalendarIntegrationsPageProps) => {
  const [{ data, fetching, error }, refetchStatus] = useQuery<CalendarIntegrationStatusQueryData>({
    query: CALENDAR_INTEGRATION_STATUS_QUERY,
    requestPolicy: "cache-and-network"
  })
  const [conflictConnection, setConflictConnection] = useState<CalendarConnectionSummary | null>(null)
  const [writeConnection, setWriteConnection] = useState<CalendarConnectionSummary | null>(null)
  const [disconnectConnection, setDisconnectConnection] = useState<CalendarConnectionSummary | null>(null)

  const refetch = () => refetchStatus({ requestPolicy: "network-only" })

  const status = data?.calendarIntegrationStatus
  const connections = status ? visibleConnections(status.connections) : []
  const migrations = status?.migrations || []
  const connectableProviders = status?.connectableProviders || []

  return (
    <div>
      <Breadcrumbs links={[{ href: "/portal/settings", text: "Settings" }]} activePage="Calendar Integrations" />
      <div className="mb-9 flex flex-col sm:mb-4">
        <Typography as="h1" variant="h3" className="">
          Calendar Integrations
        </Typography>
        <Typography as="p" variant="body" className="text-gray-dark sm:hidden">
          Sync your calendar across apps so wherever you work from, it happens here.
        </Typography>
      </div>

      {fetching && !status && (
        <div className="w-full max-w-[750px] rounded-xl border border-gray-light p-8 text-gray-dark" role="status">
          Loading your calendar connections…
        </div>
      )}

      {error && !status && (
        <div className="w-full max-w-[750px] rounded-xl border border-red bg-red/5 p-8" role="alert">
          <p>We couldn&apos;t load your calendar connections. Please refresh the page to try again.</p>
        </div>
      )}

      {status && (
        <div className="w-full max-w-[750px]">
          {connections.length === 0 && (
            <div className="mb-8 flex flex-col rounded-xl border border-gray-light p-8">
              <div className="mb-8 w-fit rounded-xl bg-gray-ultralight p-4">
                <CalendarIcon className="size-12" />
              </div>
              <Typography as="h3" variant="h5" className="mb-2">
                Sync Heal.me with your existing calendar
              </Typography>
              <Typography as="p" variant="body" className="mb-8 text-gray-dark">
                Prevent double bookings and add new Heal.me appointments to the calendar you already use.
              </Typography>
              <ConnectProviderButtons
                connectableProviders={connectableProviders}
                connections={connections}
                onConnected={refetch}
              />
              {connectableProviders.length === 0 && (
                <Typography as="p" variant="body" className="text-gray-dark">
                  Calendar connections aren&apos;t available right now. Please contact support.
                </Typography>
              )}
            </div>
          )}

          {connections.length > 0 && (
            <Box className="">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-gray-dark">
                <h2 className="text-lg font-bold">My Calendar Accounts</h2>
              </div>
              <div className="flex flex-col gap-4">
                {connections.map((connection) => (
                  <AccountCard
                    key={connection.publicId}
                    connection={connection}
                    migrations={migrations}
                    connectableProviders={connectableProviders}
                    onEditConflicts={setConflictConnection}
                    onEditWrites={setWriteConnection}
                    onDisconnect={setDisconnectConnection}
                    onChanged={refetch}
                  />
                ))}
              </div>
              {connectableProviders.length > 0 && (
                <>
                  <Divider className="" />
                  <div className="mb-2 text-sm font-bold text-gray-dark">Connect another calendar</div>
                  <ConnectProviderButtons
                    connectableProviders={connectableProviders}
                    connections={connections}
                    onConnected={refetch}
                  />
                </>
              )}
            </Box>
          )}

          {connections.length > 0 && (
            <Box className="mt-8">
              <h2 className="text-lg font-bold">Configuration</h2>
              <Divider className="" />
              <AllDayBlockingSettings externalCalendarAllDayBlocking={externalCalendarAllDayBlocking || "block_all"} />
            </Box>
          )}

          <ReadCalendarsFlyout
            key={`conflicts-${conflictConnection?.publicId || "none"}`}
            connection={conflictConnection}
            visible={Boolean(conflictConnection)}
            onClose={() => setConflictConnection(null)}
            onSaved={() => {
              setConflictConnection(null)
              refetch()
            }}
          />
          <WriteCalendarFlyout
            key={`writes-${writeConnection?.publicId || "none"}`}
            connection={writeConnection}
            visible={Boolean(writeConnection)}
            onClose={() => setWriteConnection(null)}
            onSaved={() => {
              setWriteConnection(null)
              refetch()
            }}
          />
          <DisconnectConfirmModal
            connection={disconnectConnection}
            migrations={migrations}
            onClose={() => setDisconnectConnection(null)}
            onDisconnected={() => {
              setDisconnectConnection(null)
              refetch()
            }}
          />
        </div>
      )}
    </div>
  )
}

export default function CalendarIntegrationsPage(props: CalendarIntegrationsPageProps) {
  return (
    <GraphQLProvider>
      <ToastProvider>
        <CalendarIntegrationsBody {...props} />
      </ToastProvider>
    </GraphQLProvider>
  )
}
